
import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';

const HowItWorks = () => {
  const [ref, isInView] = useInView({ threshold: 0.2 });

  const steps = [
    {
      number: '01',
      icon: 'ri-file-list-3-line',
      title: 'Submit Plans or Material List',
      description:
        'Upload your construction plans, material specifications, or project requirements. Our team reviews your needs and identifies all required materials.',
      details: [
        'Architectural plans accepted',
        'Material lists welcome',
        'Free initial consultation',
        '24-hour response time',
      ],
    },
    {
      number: '02',
      icon: 'ri-building-line',
      title: 'We Source Direct from Verified Factories',
      description:
        'We connect directly with our network of 5,000+ verified manufacturers worldwide to source your materials at factory-direct pricing.',
      details: [
        'Quality-certified factories',
        'Competitive bidding process',
        'Transparent pricing breakdown',
        'No middleman markup',
      ],
    },
    {
      number: '03',
      icon: 'ri-ship-line',
      title: 'We Manage Importing and Logistics',
      description:
        'Our logistics team handles all shipping, customs clearance, documentation, and coordination from factory to your delivery location.',
      details: [
        'Full customs clearance',
        'Shipment tracking',
        'Insurance coverage',
        'Delivery coordination',
      ],
    },
    {
      number: '04',
      icon: 'ri-truck-line',
      title: 'Materials Delivered to Your Jobsite',
      description:
        'Your materials arrive on schedule, ready for installation. We coordinate final delivery to ensure everything arrives when you need it.',
      details: [
        'On-time delivery guarantee',
        'Jobsite coordination',
        'Quality inspection',
        'Installation support available',
      ],
    },
  ];

  return (
    <section
      ref={ref}
      className="py-20 lg:py-28 bg-gradient-to-b from-white to-gray-50"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-secondary-100 text-secondary-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="ri-lightbulb-line"></i>
            <span>SIMPLE PROCESS</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From your initial plans to final delivery, we handle every step of
            the sourcing and logistics process so you can focus on building.
          </p>
        </motion.div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              className="relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100"
            >
              {/* Step Number Badge */}
              <div className="absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-xl">
                  {step.number}
                </span>
              </div>

              {/* Icon */}
              <div className="w-16 h-16 flex items-center justify-center bg-secondary-50 rounded-xl mb-6 ml-12">
                <i
                  className={`${step.icon} text-4xl text-secondary-600`}
                ></i>
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {step.title}
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                {step.description}
              </p>

              {/* Details List */}
              <ul className="space-y-3">
                {step.details.map((detail, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-secondary-600 text-lg"></i>
                    </div>
                    <span className="text-sm text-gray-700">{detail}</span>
                  </li>
                ))}
              </ul>

              {/* Connector Line (except last item) */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-6 w-12 h-0.5 bg-gradient-to-r from-secondary-300 to-transparent"></div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 md:p-12 shadow-xl">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Get Started?
            </h3>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Submit your project details today and receive a detailed cost
              comparison within 48 hours.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => {
                  const quoteButton = document.querySelector(
                    '[data-quote-trigger]'
                  ) as HTMLButtonElement | null;
                  quoteButton?.click();
                }}
                className="w-full sm:w-auto bg-white text-primary-700 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-50 transition-all hover:scale-105 shadow-lg whitespace-nowrap"
              >
                <i className="ri-file-list-3-line mr-2"></i>
                Get My Quote
              </button>
              <button
                onClick={() => {
                  const planButton = document.querySelector(
                    '[data-plan-trigger]'
                  ) as HTMLButtonElement | null;
                  planButton?.click();
                }}
                className="w-full sm:w-auto bg-primary-800 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-primary-900 transition-all hover:scale-105 shadow-lg whitespace-nowrap border-2 border-white/20"
              >
                <i className="ri-upload-cloud-line mr-2"></i>
                Upload Plans
              </button>
            </div>
          </div>
        </motion.div>

        {/* Timeline Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 1, duration: 0.6 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center space-x-2 text-gray-500 text-sm">
            <i className="ri-time-line"></i>
            <span>
              Average timeline: 6-12 weeks from quote to delivery
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
